/**
 * Buttons.
 *
 * ## Variants and states are different axes, and the spec's table hides that
 *
 * `components.md` lists six rows — Primary, Secondary, Quiet, Destructive,
 * Loading, Disabled — as if they were one set. **They are not.** Loading and
 * disabled are *states* every variant can be in; modelling them as variants
 * makes "a destructive button that is currently applying" unrepresentable,
 * which is exactly the state the confirmation dialogs need. So: four variants,
 * two independent boolean states.
 *
 * ## No recall-red destructive variant, deliberately
 *
 * `components.md` gives Destructive `--pp-error` fill *"(`--pp-recall` for
 * revoke-class actions)"*, while `acceptance-tests.md` reserves `--pp-recall`
 * for recall content and nowhere else in the product. The handoff contradicts
 * itself, and the contradiction is unresolved as of c24.
 *
 * The revoke-class variant is therefore **not built**. Its only call site is
 * the API-key screen in Phase 5/7, and building an unconsumed variant now is
 * precisely how the Phase 0 Surface vocabulary went wrong — a plausible
 * placeholder nothing imported, wrong for a whole phase.
 *
 * The recommendation on the table for Cameron is that the two non-recall uses
 * of `--pp-recall` in the handoff are a *value* choice wearing a *semantic*
 * token's name: `--pp-pill-error-fg` is `#8E1F19`, byte-identical to
 * `--pp-recall`, so the deep red is already available under an error-family
 * name. `components.md`'s own philosophy argues the same way — it says
 * *"publishing a recall is neutral — it is a responsible act, not a destructive
 * one"*, so recall red belongs to recall content, never to a destructive act.
 * Until that is ruled, this file builds `--pp-error` and stops.
 *
 * ## Only two red buttons exist in the entire product
 *
 * Revoke an API key, and delete a product. That is the whole list. If a third
 * appears, the question to ask is whether it is really destructive or merely
 * consequential — publishing a recall notice is the loudest thing a user can
 * do here and it is a **neutral** button.
 */
import { createElement } from "react";
export const BUTTON_VARIANTS = ["primary", "secondary", "quiet", "destructive"];
export function Button({ variant = "secondary", touch = false, loading = false, disabled = false, children, className, type = "button", ...rest }) {
    return createElement("button", {
        ...rest,
        type,
        className: className === undefined ? "pp-button" : `pp-button ${className}`,
        "data-variant": variant,
        "data-touch": touch ? "true" : undefined,
        "data-loading": loading ? "true" : undefined,
        // A loading button is not pressable, and saying so in the DOM rather than
        // only in CSS is what makes that true for a keyboard and a screen reader.
        disabled: disabled || loading,
        "aria-busy": loading ? "true" : undefined,
    }, children);
}
//# sourceMappingURL=Button.js.map